import type { EntryFormAction, FieldInputAction } from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { AsyncQueue } from '@/features/content/AsyncQueue';
import { useSpeak2MeStore } from '@/store/speak2meStore';
import {
  ConversationItem,
  MoveObservationMediaResult,
  ProvenanceType,
  SuggestedAction,
} from '@vivport/backend/types';
import { RotateCcw } from 'lucide-react';
import { type ReactNode, type RefObject, useMemo } from 'react';
import { ProvenanceBody, ProvenancePaneHeader, mapProvenanceDetailToHistoryItems } from '@/components/provenance';
import { ProvenanceMemberBubble } from '@/components/provenance/ProvenanceMemberBubble';
import { useProvenanceDetail } from '@/hooks/useProvenanceQueries';
import type { ObservationOption } from '@/components/provenance/observation-labels';
import { ChatTitleBar, Composer, SuggestedActionsBar } from '.';
import type { EntryFormSubmit } from './EntryFormActionCard';
import { type ComposerDictation, type VoiceSession } from '../useVoiceSession';
import { type StagedAttachment } from './ChatComposer';
import { ChatUnreadBadge } from './ChatUnreadBadge';
import { ChatSpendPill } from './ChatSpendPill';
import { ChatUnreadList } from './ChatUnreadList';
import {
  EphemeralThinkingSurface,
  type EphemeralPhase,
  type EphemeralToolCall,
} from '../EphemeralThinkingSurface';

export interface Speak2MePanelBodyProps {
  closeControl: ReactNode;
  onResetConversation: () => void;
  history: ConversationItem[];
  bottomRef: RefObject<HTMLDivElement>;
  onRequestCapture: (args: { mode: 'recording' | 'photo' | 'video'; linkContentId?: number }) => void;
  observationOptions: readonly ObservationOption[];
  /** The NeoChat provenance id, so captures file against this chat. */
  captureOriginProvenanceId?: number;
  onOpenContent: (contentId: number) => void;
  onObservationMediaMoved: (result: MoveObservationMediaResult) => void;
  suggestedActions: SuggestedAction[];
  onActionClick: (action: SuggestedAction) => void;
  onFieldInputSubmit: (action: FieldInputAction, value: string) => Promise<void>;
  onEntryFormSubmit: (action: EntryFormAction, submit: EntryFormSubmit) => Promise<void>;
  conversationPhotoIds: number[];
  disabled: boolean;
  /** Capture is gated separately: a streaming turn must not stop the member capturing. */
  captureDisabled: boolean;
  composerText: string;
  onComposerTextChange: (value: string) => void;
  canSend: boolean;
  onSend: () => void;
  onAttachFiles: (files: File[]) => void;
  stagedAttachments: StagedAttachment[];
  onRemoveStagedAttachment: (fileId: number) => void;
  isUploadingFiles: boolean;
  voiceEnabled: boolean;
  voiceSession: VoiceSession;
  dictation: ComposerDictation;
  activeProvenanceId?: number;
  activeProvenanceType?: ProvenanceType;
  activeProvenanceCode?: string;
  chromeBusy: boolean;
  sessionEpoch: number;
  ephemeralPhase: EphemeralPhase;
  ephemeralStreamingText: string;
  ephemeralToolCalls: EphemeralToolCall[];
  onCancelTurn: () => void;
  onRetryTurn: () => void;
}

export function Speak2MePanelBody({
  closeControl,
  onResetConversation,
  history,
  bottomRef,
  onRequestCapture,
  observationOptions,
  captureOriginProvenanceId,
  onOpenContent,
  onObservationMediaMoved,
  suggestedActions,
  onActionClick,
  onFieldInputSubmit,
  onEntryFormSubmit,
  conversationPhotoIds,
  disabled,
  captureDisabled,
  composerText,
  onComposerTextChange,
  canSend,
  onSend,
  onAttachFiles,
  stagedAttachments,
  onRemoveStagedAttachment,
  isUploadingFiles,
  voiceEnabled,
  voiceSession,
  dictation,
  activeProvenanceId,
  activeProvenanceType,
  activeProvenanceCode,
  chromeBusy,
  sessionEpoch,
  ephemeralPhase,
  ephemeralStreamingText,
  ephemeralToolCalls,
  onCancelTurn,
  onRetryTurn,
}: Speak2MePanelBodyProps) {
  const view = useSpeak2MeStore((state) => state.view);
  const setView = useSpeak2MeStore((state) => state.setView);
  const isNeoChatSubject = activeProvenanceType === 'NEO_CHAT' || activeProvenanceType === undefined;
  const headerLabel =
    activeProvenanceType === 'PROCESS_RUN'
      ? 'Process Run'
      : activeProvenanceType === 'MANUAL_SESSION'
        ? 'Manual Session'
        : '@Neo';
  const provenanceDetailQuery = useProvenanceDetail(
    !isNeoChatSubject ? activeProvenanceCode : undefined
  );
  const provenanceDetail = provenanceDetailQuery.data;
  const nonNeoItems = useMemo(
    () => mapProvenanceDetailToHistoryItems(provenanceDetail ?? null),
    [provenanceDetail]
  );
  const showUnreadList = isNeoChatSubject && view === 'unread-list';
  const voicePendingText =
    voiceSession.mode === 'conversation' ? voiceSession.interimTranscript : '';
  return (
    <>
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium leading-snug text-foreground">{headerLabel}</h2>
              {isNeoChatSubject ? <ChatSpendPill neoChatId={activeProvenanceId} /> : null}
              {isNeoChatSubject ? (
                <ChatUnreadBadge onClick={() => setView('unread-list')} />
              ) : null}
            </div>
            <p className="text-sm leading-snug text-muted-foreground">Reshaping Your Reality</p>
          </div>
          <div className="flex items-center gap-1">
            <AsyncQueue />
            <Button
              data-testid="speak2me-new-chat-button"
              variant="outline"
              size="sm"
              onClick={onResetConversation}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              New Chat
            </Button>
            {closeControl}
          </div>
        </div>
        {isNeoChatSubject ? (
          view === 'unread-list' ? (
            <div className="mt-1.5 text-xs leading-snug text-muted-foreground">
              NeoChats that need attention
            </div>
          ) : (
            <ChatTitleBar
              neoChatId={activeProvenanceId}
              busy={chromeBusy}
              sessionEpoch={sessionEpoch}
            />
          )
        ) : provenanceDetail ? (
          <ProvenancePaneHeader detail={provenanceDetail} />
        ) : null}
      </div>

      <Separator />

      {showUnreadList ? (
        <ChatUnreadList />
      ) : (
        <ProvenanceBody
          mode="pane"
          parentContentId={activeProvenanceId ?? 0}
          items={isNeoChatSubject ? history : nonNeoItems}
          isLoading={!isNeoChatSubject && provenanceDetailQuery.isLoading}
          isError={!isNeoChatSubject && provenanceDetailQuery.isError}
          errorMessage={
            !isNeoChatSubject && provenanceDetailQuery.error instanceof Error
              ? provenanceDetailQuery.error.message
              : 'Failed to load.'
          }
          bottomRef={isNeoChatSubject ? bottomRef : undefined}
          onRequestCapture={isNeoChatSubject ? onRequestCapture : undefined}
          onOpenContent={onOpenContent}
          onObservationMediaMoved={onObservationMediaMoved}
          ephemeralSlot={
            isNeoChatSubject && (ephemeralPhase !== 'hidden' || voicePendingText) ? (
              <>
                {voicePendingText ? (
                  <ProvenanceMemberBubble text={voicePendingText} density="compact" pending />
                ) : null}
                {ephemeralPhase !== 'hidden' ? (
                  <EphemeralThinkingSurface
                    phase={ephemeralPhase}
                    streamingText={ephemeralStreamingText}
                    toolCalls={ephemeralToolCalls}
                    onCancel={onCancelTurn}
                    onRetry={onRetryTurn}
                  />
                ) : null}
              </>
            ) : undefined
          }
          suggestedActionsSlot={
            isNeoChatSubject ? (
              <SuggestedActionsBar
                actions={suggestedActions}
                disabled={disabled}
                onActionClick={(action) => {
                  onActionClick(action);
                }}
                onFieldInputSubmit={onFieldInputSubmit}
                onEntryFormSubmit={onEntryFormSubmit}
                conversationPhotoIds={conversationPhotoIds}
              />
            ) : undefined
          }
          composerSlot={
            isNeoChatSubject ? (
              <Composer
                observationOptions={observationOptions}
                captureOriginProvenanceId={captureOriginProvenanceId}
                value={composerText}
                onChange={onComposerTextChange}
                onSend={onSend}
                canSend={canSend}
                disabled={disabled}
                captureDisabled={captureDisabled}
                placeholder={
                  voiceSession.mode === 'conversation'
                    ? voiceSession.status === 'speaking'
                      ? 'Neo is speaking…'
                      : voiceSession.status === 'suspended'
                        ? 'Neo is thinking…'
                        : 'Listening…'
                    : 'Next instruction...'
                }
                onAttachFiles={onAttachFiles}
                stagedAttachments={stagedAttachments}
                onRemoveStagedAttachment={onRemoveStagedAttachment}
                isUploadingFiles={isUploadingFiles}
                voiceEnabled={voiceEnabled}
                dictation={dictation}
                isConversationActive={voiceSession.mode === 'conversation'}
                voiceStatus={voiceSession.status}
                voiceError={voiceSession.lastError}
                onConversationToggle={
                  voiceSession.mode === 'conversation'
                    ? voiceSession.stop
                    : voiceSession.startConversation
                }
                neoChatId={activeProvenanceId}
              />
            ) : undefined
          }
        />
      )}
    </>
  );
}
